import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Typo, Button, Flex, Textinput, theme, Modal } from '../../ui';
import { useIsLoggedIn, useUser } from '../../features/auth/hooks';

import { useInvitesModalState, usePendingGiftCode } from '../../features/invites/hooks/useInvitesState';
import { useInvitesActions } from '../../features/invites/hooks/useInvitesActions';
import { useNavigate } from 'react-router';
import { useSetLoginModal } from '../../features/global/hooks';
import RegistrationForm from '../RegistrationForm';
import { useTranslation } from 'react-i18next';
import { useRegisterAndLogin } from '../../features/auth/hooks/useAuthActions';

const ButtonStyled = styled(Button)`
  align-self:flex-end;
`

const GiftForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setLoginModal = useSetLoginModal();
  const isLoggedIn = useIsLoggedIn();
  const user = useUser();
  const userName = user?.displayName;

  const registrationAndLogin = useRegisterAndLogin();
  const { checkInvitation, acceptInvitation, setPendingGiftCode } = useInvitesActions();
  const inputRef = useRef();
  const [registerFormOpen, setRegisterFormOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showGiftCodeErrorModal, setShowGiftCodeErrorModal] = useState(false);
  const { modalState } = useInvitesModalState();
  const pendingGiftCode = usePendingGiftCode();
  const invitationFeedback = modalState?.redeemInvitation || {};
  const { course, used, usedByMe, errMsg, giftCode, expired } = invitationFeedback;

  const handleCheckGiftCode = async () => {
    setErrorMessage(null);
    try {
      const result = await checkInvitation({ giftCode: inputRef.current.value });
      const invitation = (result && Array.isArray(result)) ? result[0] : undefined;
      const expired = invitation?.expired;
      const used = invitation?.used;
      const code = invitation?.giftCode;
      if (!isLoggedIn && !used && !expired) {
        setPendingGiftCode(code);
        setRegisterFormOpen(true);
      } else if (isLoggedIn && !used && !expired) {
        handleAccept(code);
      } else if (used) {
        setErrorMessage(t('gift.used'));
        setRegisterFormOpen(false);
      } else if (expired) {
        setErrorMessage(t('gift.expired'));
        setRegisterFormOpen(false);
      }
    } catch (err) {
      const serverMsg = err?.data?.errMsg || modalState?.giftCodeError;
      setErrorMessage(serverMsg || t('globals.genericError'));
      setRegisterFormOpen(false);
    }
  }

  const handleKeyPress = (event) => {
    const newCode = inputRef.current.value
    if (event.key === 'Enter' && newCode !== giftCode) {
      setErrorMessage(null)
      handleCheckGiftCode()
    }
  }

  const handleAccept = async (code) => {
    if (code) {
      const result = await acceptInvitation({ giftCode: code });
      if (!result.errorCode) {
        navigate(`/dashboard`);
      } else {
        setErrorMessage(result.response.errMsg);
      }
    } else {
      setErrorMessage(t('globals.genericError'));
    }
  }

  const handleLogin = () => {
    // Store the current invitation hash so we can redirect back after login
    const currentPath = window.location.pathname;
    const invitationHash = currentPath.split('/invitation/')[1];
    if (invitationHash) {
      localStorage.setItem('pendingInvitationHash', invitationHash);
    }
    if (giftCode) {
      setPendingGiftCode(giftCode);
    }
    setLoginModal(true)
  }

  const handleCallback = async () => {
    if (pendingGiftCode) {
      await acceptInvitation({ giftCode: pendingGiftCode });
      setPendingGiftCode(null);
    }
  }


  return (
    <>
      <Typo.ContentSmallHeader>{t('gift.hasGiftCode')}</Typo.ContentSmallHeader>

      <Typo.Text>{t('gift.acceptSubText')}</Typo.Text>
      <Flex direction='column' width='100%' align='flex-start'>
        <Textinput inputRef={inputRef} onKeyPress={handleKeyPress} />
        {errorMessage && <Typo.Info color={theme.colors.ERROR_RED} fontWeight='400'>{errorMessage}</Typo.Info>}
      </Flex>
      {isLoggedIn &&
        <Flex direction='column' align='flex-start' gap='10'>
          <Typo.Text>{t('gift.loggedIn', { userName: userName })}</Typo.Text>
          {!used && <Typo.Text>{t('gift.courseAdded')}</Typo.Text>}
        </Flex>
      }
      {!registerFormOpen && (!course || errorMessage) && <ButtonStyled icon='Gift' onClick={handleCheckGiftCode}>{t('globals.gift')}</ButtonStyled>}
      {registerFormOpen && !isLoggedIn && !errMsg && <RegistrationForm handleLogin={handleLogin} createAccountCall={registrationAndLogin} callbackFn={handleCallback} />}
      {!isLoggedIn && !registerFormOpen &&
        <Flex direction='column'>
          <Typo.Info>{t('gift.needCoursioAccount')}</Typo.Info>
          <Typo.Info>{t('gift.coursioExplanation')}</Typo.Info>
        </Flex>
      }

      {isLoggedIn && usedByMe && <Typo.Text>{t('gift.courseLocation')}</Typo.Text>}
      {isLoggedIn && !used && course && <ButtonStyled onClick={() => handleAccept(giftCode)}>{t('gift.acceptCourse')}</ButtonStyled>}

      <Modal
        open={showGiftCodeErrorModal}
        onClose={() => setShowGiftCodeErrorModal(false)}
        title={t('gift.acceptFailed')}
        showActions={true}
        onAccept={() => {
          setShowGiftCodeErrorModal(false);
          navigate('/profile/gift');
        }}
        acceptLabel={t('gift.goToProfile')}
      >
        <Flex direction='column' gap='10'>
          <Typo.Text>{t('gift.acceptFailedMessage')}</Typo.Text>
          <Typo.Text>{t('gift.acceptManually')}</Typo.Text>
        </Flex>
      </Modal>
    </>
  );
}

export default GiftForm;
